// Zustand 5 UI-only store per ADR-013 §8. Server-derived state belongs in
// TanStack Query (src/api/queryClient.ts); this store holds pure UI:
// drawer open/closed and left-rail collapse.
//
// activeRole is GONE (SEM-5131 owner decision 2026-07-30): there is no hat —
// one sidebar whose groups are filtered by the caller's real authority
// (LeftRail reads effective permissions + scope tier directly). Do not
// reintroduce a role/menu switch here; the menu manifest lives in
// components/menu-config.ts and visibility is server-derived.

import { create } from "zustand";

const RAIL_SECTIONS_STORAGE_KEY = "novemai.rail.expanded-sections";

/**
 * Which rail groups the user has opened past their first few entries.
 *
 * localStorage rather than session: this is a layout preference about the
 * user's own product, and re-collapsing the group they work in on every
 * sign-in would be the same "the app forgot" complaint the truncation exists to
 * answer. An unreadable store (private mode) degrades to "nothing opened" —
 * every destination is still one click away behind its group toggle.
 */
function readExpandedSections(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(RAIL_SECTIONS_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

function persistExpandedSections(keys: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(RAIL_SECTIONS_STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    /* best effort — the in-memory Set still holds for this page load */
  }
}

interface UiState {
  leftRailExpanded: boolean;
  /** Rail group keys the user opened in full (see readExpandedSections). */
  expandedRailSections: ReadonlySet<string>;
  toggleRailSection: (key: string) => void;
  /**
   * Open/closed for the İK Asistanı panel (features/support-assistant). Named
   * for the assistant, not for a "drawer": the Faz-0 AiDrawer it replaced hit
   * the unmanaged raw-prompt endpoint and is gone (2026-08-04) — the panel the
   * TopBar button opens now is the capability-dispatched one.
   */
  aiAssistantOpen: boolean;
  /**
   * Dar ekranda kenar çubuğunun üstte açılıp açılmadığı (SEM-4928).
   *
   * `leftRailExpanded`'dan AYRI bir eksen, aynı bayrağa bindirilmedi: geniş
   * ekranda soru "çubuk geniş mi dar mı", dar ekranda "çubuk var mı yok mu".
   * Tek bayrak olsaydı telefonda menüyü kapatmak masaüstünde onu ikon şeridine
   * düşürürdü.
   *
   * Kalıcı DEĞİL: bir örtü paneli, sayfa yüklenince kapalı doğmalı.
   */
  mobileNavOpen: boolean;
  toggleLeftRail: () => void;
  setLeftRailExpanded: (next: boolean) => void;
  toggleMobileNav: () => void;
  setMobileNavOpen: (next: boolean) => void;
  toggleAiAssistant: () => void;
  setAiAssistantOpen: (next: boolean) => void;
  /** The ⌘K record search dialog; the TopBar button and the rail box share it. */
  globalSearchOpen: boolean;
  setGlobalSearchOpen: (next: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  leftRailExpanded: true,
  expandedRailSections: readExpandedSections(),
  toggleRailSection: (key: string): void =>
    set((state) => {
      const next = new Set(state.expandedRailSections);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persistExpandedSections(next);
      return { expandedRailSections: next };
    }),
  aiAssistantOpen: false,
  mobileNavOpen: false,
  toggleMobileNav: (): void => set((state) => ({ mobileNavOpen: !state.mobileNavOpen })),
  setMobileNavOpen: (next: boolean): void => set({ mobileNavOpen: next }),
  toggleLeftRail: (): void =>
    set((state) => ({ leftRailExpanded: !state.leftRailExpanded })),
  setLeftRailExpanded: (next: boolean): void => set({ leftRailExpanded: next }),
  toggleAiAssistant: (): void => set((state) => ({ aiAssistantOpen: !state.aiAssistantOpen })),
  setAiAssistantOpen: (next: boolean): void => set({ aiAssistantOpen: next }),
  globalSearchOpen: false,
  setGlobalSearchOpen: (next: boolean): void => set({ globalSearchOpen: next }),
}));
